import * as client from "../../../client";
// The runtime enum comes from the generated types module directly so this
// file stays importable from unit tests (the client barrel pulls in axios).
import { peril_type } from "../../../client/types.gen";
import { readableNameFromPeril } from "../general-input/util";
import { effectiveExcerpts } from "../policy/excerpts";

/** One limit / sublimit / deductible line as the submission pane renders it. */
export type ExcerptRow = {
  id: string;
  name: string;
  value: string;
  /** Typed in by an underwriter rather than extracted from the form. */
  manual: boolean;
};

export type ExcerptGroup<R extends ExcerptRow> = {
  key: string;
  title: string;
  /** Shown beside the title, e.g. why the group is muted. */
  note?: string;
  /** Rendered dimmed: present on the report but not on this policy. */
  muted?: boolean;
  rows: R[];
};

export type PerilExcerptRow = ExcerptRow & { peril: client.peril_type };

export const PERIL_ORDER: client.peril_type[] = Object.values(peril_type);

// Keyed rows first in key order, unkeyed rows after in their existing order,
// matching the backend's _ordered_excerpts.
const bySortKey = <T extends { sortKey: string | null }>(rows: T[]): T[] =>
  rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      if (a.row.sortKey === null && b.row.sortKey === null)
        return a.index - b.index;
      if (a.row.sortKey === null) return 1;
      if (b.row.sortKey === null) return -1;
      return a.row.sortKey.localeCompare(b.row.sortKey) || a.index - b.index;
    })
    .map(({ row }) => row);

/**
 * Limit or deductible rows grouped by peril, in canonical peril order. A
 * manual entry for a peril shadows the extracted one, as the Policy Fields
 * tab and the backend's ``get_limits_for_file`` both resolve it. Perils not
 * insured on this policy still list (they are report data) but are muted.
 */
export const perilExcerptGroups = (
  extracted: Record<string, client.AdvancedLimit | client.AdvancedDeductible>,
  manual: Record<string, client.AdvancedLimit | client.AdvancedDeductible>,
  effectivePerils: readonly string[]
): ExcerptGroup<PerilExcerptRow>[] => {
  const groups: ExcerptGroup<PerilExcerptRow>[] = [];
  for (const peril of PERIL_ORDER) {
    const manualEntry = manual[peril];
    const entry = manualEntry ?? extracted[peril];
    if (!entry) continue;
    const rows = bySortKey(effectiveExcerpts(entry)).map((excerpt) => ({
      id: `${peril}::${excerpt.name}`,
      peril,
      name: excerpt.name,
      value: excerpt.value,
      manual: manualEntry !== undefined || excerpt.isOverride,
    }));
    if (rows.length === 0) continue;
    const insured = effectivePerils.includes(peril);
    groups.push({
      key: peril,
      title: readableNameFromPeril(peril),
      muted: !insured,
      note: insured ? undefined : "Not insured on this policy",
      rows,
    });
  }
  return groups;
};

export type SublimitRow = ExcerptRow & { sublimit: client.Sublimit };

export type SublimitClassification = client.Sublimit["classification"];

// The classification blocks in display order, mirroring the backend's
// SUBLIMIT_SECTIONS. The null classification is the plain sublimit schedule.
export const SUBLIMIT_BLOCKS: {
  key: string;
  classification: SublimitClassification | null;
  title: string;
}[] = [
  { key: "default", classification: null, title: "Sublimits" },
  { key: "time_limit", classification: "time_limit", title: "Time Limits" },
  {
    key: "distance_limit",
    classification: "distance_limit",
    title: "Distance Limits",
  },
  {
    key: "waiting_period",
    classification: "waiting_period",
    title: "Waiting Periods",
  },
];

export const sublimitClassificationForBlock = (
  key: string
): SublimitClassification | null =>
  SUBLIMIT_BLOCKS.find((block) => block.key === key)?.classification ?? null;

/**
 * The selected form's sublimits plus the manual ones, one group per
 * classification block, ordered by the user's drag order within each. Every
 * block is returned (possibly empty) so each has somewhere to add a row.
 */
export const sublimitGroups = (
  info: client.SublimitInfo | null,
  selectedForm: string | null
): ExcerptGroup<SublimitRow>[] => {
  const manual = (info?.manual_sublimits ?? []).map((sublimit) => ({
    sublimit,
    manual: true,
  }));
  const extracted = (
    selectedForm ? (info?.extracted_sublimits[selectedForm] ?? []) : []
  ).map((sublimit) => ({ sublimit, manual: false }));
  return SUBLIMIT_BLOCKS.map((block) => {
    const inBlock = bySortKey(
      [...manual, ...extracted]
        .filter(
          ({ sublimit }) =>
            !sublimit.is_removed &&
            (sublimit.classification ?? null) === block.classification
        )
        .map((entry) => ({
          ...entry,
          sortKey: entry.sublimit.sort_key ?? null,
        }))
    );
    return {
      key: block.key,
      title: block.title,
      // A row stored before rows had ids keys on its position, since a form
      // can repeat a name within a block.
      rows: inBlock.map(({ sublimit, manual: isManual }, index) => ({
        id:
          sublimit.id ??
          `${isManual ? "manual" : "extracted"}::${block.key}::${index}`,
        name: sublimit.sublimit_name,
        value: sublimit.manual_override ?? sublimit.sublimit_description,
        manual: isManual,
        sublimit,
      })),
    };
  });
};
